import { motion } from "framer-motion";
import { Info, ArrowRight } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { ProgrammeTimeline } from "@/components/site/ProgrammeTimeline";
import { Reveal, SectionHeading } from "@/components/site/primitives";
import { SubjectCombinations } from "@/components/site/SubjectCombinations";
import { DirectEntry } from "@/components/home/DirectEntry";
import { ApplyButton } from "@/components/site/AdmissionBadge";
import { DISCLAIMERS, IMAGES } from "@/data/content";
import { useApply } from "@/context/ApplyContext";
import { useSEO } from "@/hooks/useSite";

export function ProgrammePage({ code, title, subtitle, intro, blocks, seo }) {
  const apply = useApply();
  useSEO(seo);
  return (
    <>
      <PageHero overline={`${code} Programme`} title={title} subtitle={subtitle} crumb={code} image={IMAGES.campus} />

      <section className="py-20 sm:py-28 bg-white">
        <div className="container-x grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <span className="overline text-[#C0202A]">Overview</span>
            <p className="mt-4 text-lg leading-relaxed text-slate-700">{intro}</p>
            <div className="mt-10 space-y-8">
              {blocks.map((b, i) => (
                <Reveal key={b.t} delay={i * 0.05}>
                  <div className="border-l-2 border-[#D4AF37] pl-5">
                    <h3 className="font-serif text-xl font-bold text-[#0A1E4E]">{b.t}</h3>
                    <p className="mt-2 text-slate-600 leading-relaxed">{b.d}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="sticky top-28 overflow-hidden rounded-sm border border-[#D4AF37]/30 bg-[#0A1E4E] p-2">
              <img src={IMAGES.portrait} alt={`${code} admission flyer`} className="w-full rounded-sm object-contain" />
              <div className="bg-[#0A1E4E] p-7 text-white">
                <h4 className="font-serif text-xl font-bold text-[#D4AF37]">Ready to begin?</h4>
                <p className="mt-2 text-sm text-blue-100/80">
                  Speak with an admissions officer to confirm your eligibility and subject combination.
                </p>
                <div className="mt-5 flex flex-col gap-3">
                  <ApplyButton className="btn-red w-full" testId={`${code.toLowerCase()}-apply-button`}>Apply Now</ApplyButton>
                  <button className="btn-outline-gold w-full" onClick={() => apply?.openApply()} data-testid={`${code.toLowerCase()}-talk-officer`}>
                    Talk to an Admissions Officer
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#F7F5EF]">
        <div className="container-x">
          <SectionHeading overline="How the programme works" title={`The ${code} Journey`} align="center" />
          <div className="mt-14"><ProgrammeTimeline /></div>
          <motion.div
            initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
            className="mt-12 flex items-start gap-3 rounded-sm border-l-4 border-[#C0202A] bg-white p-5 text-sm text-slate-700 max-w-3xl mx-auto"
          >
            <Info size={18} className="mt-0.5 shrink-0 text-[#C0202A]" />
            <p>{DISCLAIMERS.directEntry}</p>
          </motion.div>
        </div>
      </section>

      <SubjectCombinations only={code} />
      <DirectEntry />

      <section className="py-16 bg-[#0A1E4E] text-white">
        <div className="container-x flex flex-col sm:flex-row items-center justify-between gap-5">
          <h3 className="font-serif text-2xl sm:text-3xl font-bold">Not sure {code} is right for you?</h3>
          <div className="flex gap-3">
            <a href="/programmes" className="btn-outline-gold">Compare JUPEB &amp; IJMB <ArrowRight size={15} /></a>
            <ApplyButton className="btn-gold" testId={`${code.toLowerCase()}-cta-apply`}>Apply Now</ApplyButton>
          </div>
        </div>
      </section>
    </>
  );
}
